import * as React from 'react';

import { CalendarDate } from '@internationalized/date';
import { LoadStatus } from '@rematter/frontend-lib-api-client';
import { LocalizedDateTime, Row, Widget } from '@rematter/ui-web';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';

import { $outboundTicketTab } from '../../../_state/scaleTicketPro.state';
import {
  BookingBadge,
  LoadStatusBadge,
  SalesOrderBadge,
} from '../../_shared/StatusBadges';
import { WeightUnitSwitch } from '../../_shared/WeightUnitSwitch';
import { GoBackButton } from '../shared/GoBackButton';
import { PrintDocumentsMenu } from '../shared/PrintDocumentsMenu';
import { ScaleTicketSettingsPopover } from '../shared/ScaleTicketSettingsPopover';
import {
  DocumentNumber,
  TicketAccountDetailsRow,
  TicketDetailsRow,
  TicketTabsRow,
} from '../shared/TicketHeaderLayout';

import { OutboundTicketHeaderActions } from './OutboundTicketHeaderActions';

import {
  $outboundCustomerFormById,
  $updateOutboundCustomerSelectField,
  $updateOutboundCustomerSelectFields,
  DEFAULT_OUTBOUND_CUSTOMER_FORM,
} from '~/modules/trade/_state/scaleTicketOutboundCustomerForm.state';
import {
  $outboundDetailsFormById,
  $updateOutboundDetailsField,
  DEFAULT_OUTBOUND_DETAILS_FORM,
} from '~/modules/trade/_state/scaleTicketOutboundInfoForm.state';
import { $outboundWeightUnitMode } from '~/modules/trade/_state/scaleTicketOutboundMaterialsForm.state';
import {
  $outboundNotesFormById,
  $updateOutboundNotesField,
  DEFAULT_OUTBOUND_NOTES_FORM,
} from '~/modules/trade/_state/scaleTicketOutboundNotesForm.state';
import { useTranslations } from '~/modules/trade/i18n/useTranslations';
import { LoadRecord } from '~/modules/trade/types/load';
import {
  PrintableDocument,
  ScaleTicketTab,
} from '~/modules/trade/types/scaleTickets';
import { PagePresence } from '~/shared/components/PagePresence';
import {
  SelectCustomerLocationPairTouch,
  type SelectableCustomerLocationPair,
} from '~/shared/components/select-fields/SelectCustomerLocationPairTouch';
import { SelectFacilityTouch } from '~/shared/components/select-fields/SelectFacilityTouch';

const NOTES_FIELD_MAP = {
  internalNotes: 'recyclerInternalNotes',
  externalNotes: 'recyclerExternalNotes',
} as const;

type OutboundTicketHeaderProps = {
  ticket: LoadRecord | null;
  status?: LoadStatus;
  materialCount?: number;
  onShip: () => void;
  onVoid: (voidReason?: string) => void;
  onRevert: (targetStatus: LoadStatus) => void;
  onReceive: (receivedDate: CalendarDate) => void;
  onReconcile: () => void;
  isProcessing: boolean;
  reviewed: boolean;
  showReconciliation: boolean;
  onGoBack: () => void;
  printDocuments: PrintableDocument[];
};

//
// ⚛️ ⚛️ ⚛️ OUTBOUND TICKET HEADER ⚛️ ⚛️ ⚛️
//
export const OutboundTicketHeader = ({
  ticket,
  status,
  materialCount,
  onShip,
  onVoid,
  onRevert,
  onReceive,
  onReconcile,
  isProcessing,
  reviewed,
  onGoBack,
  printDocuments,
}: OutboundTicketHeaderProps) => {
  // Translations
  const t = useTranslations();

  // States
  const [resolvedCustomerName, setResolvedCustomerName] = React.useState<
    string | undefined
  >(undefined);

  // Atoms
  const detailsFormMap = useAtomValue($outboundDetailsFormById);
  const notesFormMap = useAtomValue($outboundNotesFormById);
  const customerFormMap = useAtomValue($outboundCustomerFormById);
  const [tab, setTab] = useAtom($outboundTicketTab);
  const [weightUnitMode, setWeightUnitMode] = useAtom($outboundWeightUnitMode);
  const updateDetailsField = useSetAtom($updateOutboundDetailsField);
  const updateOutboundNotesField = useSetAtom($updateOutboundNotesField);
  const updateCustomerSelectField = useSetAtom(
    $updateOutboundCustomerSelectField,
  );
  const updateCustomerSelectFields = useSetAtom(
    $updateOutboundCustomerSelectFields,
  );

  // Memoized values
  const ticketId = ticket?.id;
  const detailsForm =
    (ticketId ? detailsFormMap.get(ticketId) : undefined) ??
    DEFAULT_OUTBOUND_DETAILS_FORM;
  const outboundNotesForm =
    (ticketId ? notesFormMap.get(ticketId) : undefined) ??
    DEFAULT_OUTBOUND_NOTES_FORM;
  const customerForm =
    (ticketId ? customerFormMap.get(ticketId) : undefined) ??
    DEFAULT_OUTBOUND_CUSTOMER_FORM;
  const notesFields = React.useMemo(
    () => ({
      internalNotes: outboundNotesForm.recyclerInternalNotes,
      externalNotes: outboundNotesForm.recyclerExternalNotes,
    }),
    [
      outboundNotesForm.recyclerInternalNotes,
      outboundNotesForm.recyclerExternalNotes,
    ],
  );
  const activeTab = (tab || 'materials') as ScaleTicketTab;
  const customerName =
    resolvedCustomerName ??
    ticket?.customerName ??
    t.scaleTicketPro.detail.unknownCustomer;
  const createdByLabel = ticket?.createdByUser
    ? `${ticket.createdByUser.firstName} ${ticket.createdByUser.lastName}`
    : null;
  const weightUnitValue = weightUnitMode === 'scale' ? 'scale' : 'price';
  const showMaterialControls =
    status !== 'Shipped' &&
    status !== 'Received' &&
    status !== 'Voided' &&
    status !== 'Reconciled';

  // Callbacks
  const handleUpdateNotesField = React.useCallback(
    ({
      field,
      value,
      ticketId: tid,
    }: {
      field: 'internalNotes' | 'externalNotes';
      value: string;
      ticketId: string;
    }) => {
      updateOutboundNotesField({
        field: NOTES_FIELD_MAP[field],
        value,
        ticketId: tid,
      });
    },
    [updateOutboundNotesField],
  );

  const handleCustomerLocationChange = React.useCallback(
    (value: string | undefined, entity?: SelectableCustomerLocationPair) => {
      if (!ticketId) return;
      setResolvedCustomerName(entity?.account?.name ?? undefined);
      const customerChanged =
        (entity?.accountId ?? null) !== customerForm.customerAccountId;
      updateCustomerSelectFields({
        updates: {
          customerAccountId: entity?.accountId || null,
          customerAccountAddressId: value || null,
          ...(customerChanged ? { salesOrderId: null } : {}),
        },
        ticketId,
      });
    },
    [ticketId, customerForm.customerAccountId, updateCustomerSelectFields],
  );

  const handleEntityResolved = React.useCallback(
    (entity: SelectableCustomerLocationPair) => {
      setResolvedCustomerName(entity.account?.name ?? undefined);
    },
    [],
  );

  const handleFacilityChange = React.useCallback(
    (value: string | undefined) => {
      if (!ticketId) return;
      updateCustomerSelectField({
        field: 'facilityId',
        value: value ?? null,
        ticketId,
      });
    },
    [ticketId, updateCustomerSelectField],
  );

  const handleSalesOrderChange = React.useCallback(
    (value?: string | null) => {
      if (!ticketId) return;
      updateCustomerSelectFields({
        updates: {
          salesOrderId: value || null,
        },
        ticketId,
      });
    },
    [ticketId, updateCustomerSelectFields],
  );

  const handleTabChange = React.useCallback(
    (t: string) => setTab(t as ScaleTicketTab),
    [setTab],
  );

  const handleWeightUnitChange = React.useCallback(
    (mode: 'scale' | 'price') => {
      setWeightUnitMode(mode === 'scale' ? 'scale' : 'manual');
    },
    [setWeightUnitMode],
  );

  return (
    <Widget className="@OutboundTicketHeader mb-1 flex shrink-0 flex-col">
      {ticket?.loadNumber && (
        <Row gap2 yCenter className="px-3 pt-2 pb-1">
          <DocumentNumber documentNumber={ticket.loadNumber} />
          {ticket?.salesOrder?.documentNumber && (
            <SalesOrderBadge
              salesOrderNumber={ticket.salesOrder?.documentNumber}
              counterPartyPurchaseOrderNumber={
                ticket.salesOrder?.counterPartyPurchaseOrderNumber || ''
              }
            />
          )}
          {ticket?.bookingNumber && (
            <BookingBadge bookingNumber={ticket.bookingNumber} />
          )}
          {status && <LoadStatusBadge status={status} isReviewed={reviewed} />}
          {createdByLabel && (
            <span className="text-xs font-normal text-neutral/50">
              {createdByLabel}{' '}
              <LocalizedDateTime
                date={ticket?.timestamps.createdAt}
                dateStyle="relative-within-week"
                timeStyle="short"
              />
            </span>
          )}
          <div className="ml-auto">
            <ScaleTicketSettingsPopover />
          </div>
        </Row>
      )}

      <Row yCenter xBetween gap2 className="px-1.5 pb-1.5">
        <GoBackButton onGoBack={onGoBack} isDisabled={isProcessing} />
        <TicketAccountDetailsRow avatarName={customerName}>
          <SelectCustomerLocationPairTouch
            value={customerForm.customerAccountAddressId}
            size="xl"
            onChange={handleCustomerLocationChange}
            onEntityResolved={handleEntityResolved}
            placeholder={t.scaleTicketPro.detail.selectCustomerPlaceholder}
          />

          <SelectFacilityTouch
            value={customerForm.facilityId}
            size="xl"
            onChange={handleFacilityChange}
            placeholder={t.scaleTicketPro.detail.selectFacilityPlaceholder}
          />
        </TicketAccountDetailsRow>

        <Row gap2 yCenter xEnd>
          <PrintDocumentsMenu
            documents={printDocuments}
            isDisabled={isProcessing}
          />

          {status && (
            <OutboundTicketHeaderActions
              status={status}
              isProcessing={isProcessing}
              reviewed={reviewed}
              onShip={onShip}
              onVoid={onVoid}
              onRevert={onRevert}
              onReceive={onReceive}
              onReconcile={onReconcile}
            />
          )}
        </Row>
      </Row>

      <TicketDetailsRow
        fields={detailsForm}
        ticket={ticket}
        onUpdateFields={updateDetailsField}
        notesFields={notesFields}
        onUpdateNotesField={handleUpdateNotesField}
        showSalesOrder
        salesOrderId={customerForm.salesOrderId}
        customerAccountId={customerForm.customerAccountId}
        onSalesOrderChange={handleSalesOrderChange}
      />

      <TicketTabsRow
        context="outbound"
        materialCount={materialCount}
        activeTab={activeTab}
        onTabChange={handleTabChange}
        rightContent={
          <>
            <PagePresence />
            {showMaterialControls && (
              <WeightUnitSwitch
                value={weightUnitValue}
                onChange={handleWeightUnitChange}
              />
            )}
          </>
        }
      />
    </Widget>
  );
};
